// ============================================================
//  HomeHubScene — Production Home Hub
//
//  Approved production art composes the environment. Phaser supplies
//  responsive placement, restrained motion, interaction, and compact HUD.
//
//  Depth map:
//    0  : cosmic-home-bg
//    30 : world islands and labels
//    50 : production platform
//    60 : Cosmo
//    70 : logo and foreground crystals
//    80 : primary actions
//    90 : HUD and navigation
//    100: speech bubble
// ============================================================
import { CS } from '../../constants/Colors.js';
import { SaveManager } from '../../state/SaveManager.js';
import { audio } from '../../audio/AudioManager.js';
import { STORY_FOREST_PRODUCTION_READY } from '../worlds/story-forest/StoryForestAssets.js';
import { DISCOVERY_LAB_ROUTE_ENABLED } from '../worlds/discovery-lab/DiscoveryLabConfig.js';
import { isDiscoveryLabOpeningReady } from '../worlds/discovery-lab/DiscoveryLabAssets.js';
import { pwaUpdates } from '../../pwa/PwaUpdateManager.js';
import { canEnterNumberForge } from '../worlds/number-kingdom/districts/addition-citadel/number-forge/NumberForgeAssets.js';
import { NUMBER_FORGE_SCENE_KEY } from '../worlds/number-kingdom/districts/addition-citadel/number-forge/NumberForgeScene.js';
import { canEnterRegroupingWorkshop } from '../worlds/number-kingdom/districts/addition-citadel/regrouping-workshop/RegroupingWorkshopAssets.js';
import { REGROUPING_WORKSHOP_SCENE_KEY } from '../worlds/number-kingdom/districts/addition-citadel/regrouping-workshop/RegroupingWorkshopScene.js';
import { canEnterCharacterGrove } from '../worlds/story-forest/minigames/character-grove/CharacterGroveAssets.js';
import { CHARACTER_GROVE_SCENE_KEY } from '../worlds/story-forest/CharacterGroveScene.js';
import { canEnterMaterialsWorkshop } from '../worlds/discovery-lab/materials-workshop/MaterialsWorkshopAssets.js';
import { MATERIALS_WORKSHOP_SCENE_KEY } from '../worlds/discovery-lab/materials-workshop/MaterialsWorkshopScene.js';
import { canEnterSubtractionKeep } from '../worlds/number-kingdom/districts/subtraction-keep/SubtractionKeepAssets.js';
import { SUBTRACTION_KEEP_SCENE_KEY } from '../worlds/number-kingdom/districts/subtraction-keep/SubtractionKeepScene.js';
import { canEnterMeasurementMarket } from '../worlds/number-kingdom/districts/measurement-market/MeasurementMarketAssets.js';
import { MEASUREMENT_MARKET_SCENE_KEY } from '../worlds/number-kingdom/districts/measurement-market/MeasurementMarketScene.js';
import { canEnterClocktowerPlaza } from '../worlds/number-kingdom/districts/clocktower-plaza/ClocktowerPlazaAssets.js';
import { CLOCKTOWER_PLAZA_SCENE_KEY } from '../worlds/number-kingdom/districts/clocktower-plaza/ClocktowerPlazaScene.js';
import { canEnterCoinmakersVault } from '../worlds/number-kingdom/districts/coinmakers-vault/CoinmakersVaultAssets.js';
import { COINMAKERS_VAULT_SCENE_KEY } from '../worlds/number-kingdom/districts/coinmakers-vault/CoinmakersVaultScene.js';
import { canEnterRoyalChallengeHall } from '../worlds/number-kingdom/districts/royal-challenge-hall/RoyalChallengeHallAssets.js';
import { ROYAL_CHALLENGE_HALL_SCENE_KEY } from '../worlds/number-kingdom/districts/royal-challenge-hall/RoyalChallengeHallScene.js';
import { canEnterSequenceStream } from '../worlds/story-forest/sequence-stream/SequenceStreamAssets.js';
import { SEQUENCE_STREAM_SCENE_KEY } from '../worlds/story-forest/sequence-stream/SequenceStreamScene.js';
import { canEnterMainIdeaMeadow } from '../worlds/story-forest/main-idea-meadow/MainIdeaMeadowAssets.js';
import { MAIN_IDEA_MEADOW_SCENE_KEY } from '../worlds/story-forest/main-idea-meadow/MainIdeaMeadowScene.js';
import { canEnterInferenceHollow } from '../worlds/story-forest/inference-hollow/InferenceHollowAssets.js';
import { INFERENCE_HOLLOW_SCENE_KEY } from '../worlds/story-forest/inference-hollow/InferenceHollowScene.js';
import { canEnterAuthorsCabin } from '../worlds/story-forest/authors-cabin/AuthorsCabinAssets.js';
import { AUTHORS_CABIN_SCENE_KEY } from '../worlds/story-forest/authors-cabin/AuthorsCabinScene.js';
import { canEnterGrammarGlade } from '../worlds/story-forest/grammar-glade/GrammarGladeAssets.js';
import { GRAMMAR_GLADE_SCENE_KEY } from '../worlds/story-forest/grammar-glade/GrammarGladeScene.js';
import { canEnterFluencyLanternPath } from '../worlds/story-forest/fluency-lantern-path/FluencyLanternAssets.js';
import { FLUENCY_LANTERN_SCENE_KEY } from '../worlds/story-forest/fluency-lantern-path/FluencyLanternScene.js';
import { canEnterGreatStoryTree } from '../worlds/story-forest/great-story-tree/GreatStoryTreeAssets.js';
import { GREAT_STORY_TREE_SCENE_KEY } from '../worlds/story-forest/great-story-tree/GreatStoryTreeScene.js';
import { canEnterWeatherWatchStation } from '../worlds/discovery-lab/weather-watch-station/WeatherWatchAssets.js';
import { WEATHER_WATCH_SCENE_KEY } from '../worlds/discovery-lab/weather-watch-station/WeatherWatchScene.js';
import { canEnterHabitatFieldCamp } from '../worlds/discovery-lab/habitat-field-camp/HabitatFieldAssets.js';
import { HABITAT_FIELD_SCENE_KEY } from '../worlds/discovery-lab/habitat-field-camp/HabitatFieldScene.js';
import { canEnterPlantGrowthHouse } from '../worlds/discovery-lab/plant-growth-house/PlantGrowthAssets.js';
import { PLANT_GROWTH_SCENE_KEY } from '../worlds/discovery-lab/plant-growth-house/PlantGrowthScene.js';
import { canEnterLifeCyclePond } from '../worlds/discovery-lab/life-cycle-pond/LifeCycleAssets.js';
import { LIFE_CYCLE_SCENE_KEY } from '../worlds/discovery-lab/life-cycle-pond/LifeCycleScene.js';
import { canEnterSoundVibrationDeck } from '../worlds/discovery-lab/sound-vibration-deck/SoundVibrationAssets.js';
import { SOUND_VIBRATION_SCENE_KEY } from '../worlds/discovery-lab/sound-vibration-deck/SoundVibrationScene.js';
import { canEnterEarthMaterialsYard } from '../worlds/discovery-lab/earth-materials-yard/EarthMaterialsAssets.js';
import { EARTH_MATERIALS_SCENE_KEY } from '../worlds/discovery-lab/earth-materials-yard/EarthMaterialsScene.js';
import { canEnterDiscoveryObservatory } from '../worlds/discovery-lab/discovery-observatory/DiscoveryObservatoryAssets.js';
import { DISCOVERY_OBSERVATORY_SCENE_KEY } from '../worlds/discovery-lab/discovery-observatory/DiscoveryObservatoryScene.js';

const FONT_UI    = '"Nunito", Arial, sans-serif';
const LAYOUT_W = 1280;
const LAYOUT_H = 720;

interface IslandDef {
  x: number; y: number;
  label: string;
  glowColor: number;
  scene: string;
  locked: boolean;
  world: string;
  textureKey: string;
  artScale?: number;
}

interface ProductionButtonOptions {
  x: number;
  y: number;
  width: number;
  textureKey: string;
  depth: number;
  onActivate: () => void;
  hoverScale?: number;
}

export class HomeHubScene extends Phaser.Scene {
  private cosmoImg!: Phaser.GameObjects.Image;
  private readonly cosmoBaseScale = 0.24;
  private renderScale = 1;
  private reducedMotion = false;
  private speechBg:    Phaser.GameObjects.Graphics | null = null;
  private speechText:  Phaser.GameObjects.Text    | null = null;
  private speechTimer: Phaser.Time.TimerEvent     | null = null;

  private islands: Phaser.GameObjects.Container[] = [];
  private selectedIsland: string | null = null;

  private pxIslands!: Phaser.GameObjects.Container;
  private pxFG!:      Phaser.GameObjects.Container;

  constructor() { super({ key: 'HomeHub' }); }

  // ─────────────────────────────────────────────────────────
  create() {
    // Phaser reuses scene instances after returning from a world. Clear only
    // transient Home Hub references so the hover/click guards start fresh.
    this.islands = [];
    this.selectedIsland = null;
    this.speechBg = null;
    this.speechText = null;
    this.speechTimer = null;
    pwaUpdates.setSafeBoundary(true);
    void audio.enterSoundscape('home-hub');
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      pwaUpdates.setSafeBoundary(false);
      audio.leaveSoundscape({ id: 'home-hub' });
    });
    this.renderScale = this.configureCrispRenderer();
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    const W = LAYOUT_W, H = LAYOUT_H;

    this.pxIslands = this.add.container(0, 0).setDepth(30);
    this.pxFG      = this.add.container(0, 0).setDepth(70);

    this.buildBackground(W, H);
    this.buildLogo(W, H);
    this.buildIslands(W, H);
    this.buildPlatform(W, H);
    this.buildCosmo(W, H);
    this.buildForegroundCrystals(W, H);
    this.buildUI(W, H);
    this.setupParallax();
    this.applyTextResolution(this.renderScale);

    this.time.delayedCall(700, () => {
      this.say('Tap to enter Number Kingdom!', 4200);
    });

    const nk = SaveManager.getWorld('number-kingdom');
    if (!nk.restored && nk.bridgeSegments < 4) {
      this.time.delayedCall(1600, () => this.flickerNK());
    }
  }

  // Render the fixed logical composition into a display-sized drawing buffer.
  // Phaser FIT then maps that buffer to CSS pixels without stretching 1280x720.
  private configureCrispRenderer() {
    const parentW = this.scale.parentSize.width || window.innerWidth;
    const parentH = this.scale.parentSize.height || window.innerHeight;
    const cssScale = Math.min(parentW / LAYOUT_W, parentH / LAYOUT_H);
    const dpr = Phaser.Math.Clamp(window.devicePixelRatio || 1, 1, 2);
    const renderScale = Phaser.Math.Clamp(cssScale * dpr, 1, 2);

    this.scale.scaleMode = Phaser.Scale.FIT;
    this.scale.autoCenter = Phaser.Scale.CENTER_BOTH;
    this.scale.displaySize.setAspectMode(Phaser.Scale.FIT);
    this.scale.setGameSize(
      Math.round(LAYOUT_W * renderScale),
      Math.round(LAYOUT_H * renderScale),
    );
    this.scale.refresh();
    this.cameras.main.setZoom(renderScale).centerOn(LAYOUT_W * 0.5, LAYOUT_H * 0.5);
    return renderScale;
  }

  private applyTextResolution(resolution: number) {
    const visit = (gameObject: Phaser.GameObjects.GameObject) => {
      if (gameObject instanceof Phaser.GameObjects.Text) {
        gameObject.setResolution(resolution);
      } else if (gameObject instanceof Phaser.GameObjects.Container) {
        gameObject.list.forEach(child => visit(child));
      }
    };
    this.children.list.forEach(gameObject => visit(gameObject));
  }

  // ══════════════════════════════════════════════════════════
  // BACKGROUND — one crisp, cover-scaled production image
  // ══════════════════════════════════════════════════════════
  private buildBackground(W: number, H: number) {
    const bg = this.add.image(W * 0.5, H * 0.5, 'cosmic-home-bg').setDepth(0);
    const coverScale = Math.max(W / bg.width, H / bg.height);
    bg.setScale(coverScale);
  }

  // ══════════════════════════════════════════════════════════
  // LOGO
  // ══════════════════════════════════════════════════════════
  private buildLogo(W: number, H: number) {
    const targetWidth = W * 0.346;
    const targetHeight = targetWidth * (831 / 2000);
    this.add.image(W * 0.5, H * 0.126, 'home-logo')
        .setDisplaySize(targetWidth, targetHeight)
        .setDepth(70);
  }

  // ══════════════════════════════════════════════════════════
  // WORLD ISLANDS — real art
  // ══════════════════════════════════════════════════════════
  private buildIslands(W: number, H: number) {
    const responsiveScale = Math.min(W / 1280, H / 720);
    const defs: IslandDef[] = [
      { x: W*0.25, y: H*0.40, label: 'Number Kingdom',
        glowColor: 0xff8c00, scene: 'NumberKingdom', locked: false,
        world: 'number-kingdom', textureKey: 'number-kingdom', artScale: 0.195 * responsiveScale },
      { x: W*0.16, y: H*0.64, label: 'Story Forest',
        glowColor: 0x00c988, scene: 'StoryForest', locked: false,
        world: 'story-forest', textureKey: 'story-forest', artScale: 0.165 * responsiveScale },
      { x: W*0.80, y: H*0.40, label: 'Discovery Lab',
        glowColor: 0x00c8e8, scene: 'DiscoveryLab', locked: false,
        world: 'discovery-lab', textureKey: 'discovery-lab', artScale: 0.195 * responsiveScale },
      { x: W*0.87, y: H*0.60, label: 'Coming Soon',
        glowColor: 0x444466, scene: '', locked: true,
        world: '', textureKey: 'locked-world', artScale: 0.155 * responsiveScale },
    ];

    const floatDurations = [5200, 6100, 5700, 6500];
    defs.forEach((def, i) => {
      const island = this.buildIsland(def);
      this.islands.push(island);
      this.pxIslands.add(island);
      const baseY = def.y;
      if (!this.reducedMotion) {
        this.tweens.add({
          targets: island, y: baseY - 6,
          duration: floatDurations[i], ease: 'Sine.easeInOut',
          yoyo: true, repeat: -1, delay: i * 520,
        });
      }
    });
  }

  private buildIsland(def: IslandDef): Phaser.GameObjects.Container {
    const { glowColor, scene, locked, world, textureKey, artScale = 0.20 } = def;
    const floatRoot = this.add.container(def.x, def.y);
    const c = this.add.container(0, 0);
    floatRoot.add(c);
    const HALF_W = (1448 * artScale) / 2;
    const HALF_H = (1086 * artScale) / 2;

    // A low-alpha copy of the production silhouette supplies the hover rim.
    // It remains invisible at rest and never passes through a RenderTexture.
    const glow = this.add.image(0, 0, textureKey)
        .setOrigin(0.5, 0.62)
        .setScale(artScale * 1.035)
        .setTint(locked ? 0x7868a8 : glowColor)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setAlpha(0);
    const img = this.add.image(0, 0, textureKey)
        .setOrigin(0.5, 0.62)
        .setScale(artScale);
    c.add([glow, img]);

    // Locked art remains legible while reading as unavailable.
    if (locked) {
      img.setAlpha(0.82);
      img.setTint(0xaeb7d2);
    }

    // Compact labels stay visually separate from the production art.
    const labelY = locked ? HALF_H * 0.70 : HALF_H * 0.55;
    if (locked) {
      const pill = this.add.graphics();
      pill.fillStyle(0x130a38, 0.78);
      pill.fillRoundedRect(-66, labelY - 3, 132, 29, 14);
      pill.lineStyle(1.5, 0x9d7ee8, 0.82);
      pill.strokeRoundedRect(-66, labelY - 3, 132, 29, 14);
      pill.fillStyle(0xffffff, 0.08);
      pill.fillRoundedRect(-60, labelY, 120, 8, 8);
      c.add(pill);
    }
    c.add(this.add.text(0, labelY + (locked ? 12 : 0), def.label, {
      fontFamily: FONT_UI, fontSize: locked ? '14px' : '15px', fontStyle: 'bold',
      color: locked ? '#eee8ff' : '#ffffff',
      stroke: locked ? '#33205f' : '#0d0235', strokeThickness: locked ? 2 : 4,
    }).setOrigin(0.5, locked ? 0.5 : 0));

    // Star badge
    if (!locked && world) {
      const save = SaveManager.getWorld(world);
      const starsEarned = save.collectibles.length;
      const bg = this.add.graphics();
      const badgeY = labelY + 22;
      bg.fillStyle(0x0d0235, 0.9);
      bg.fillRoundedRect(-52, badgeY, 104, 26, 12);
      bg.lineStyle(1.5, glowColor, 0.6);
      bg.strokeRoundedRect(-52, badgeY, 104, 26, 12);
      c.add(bg);
      c.add(this.add.text(0, badgeY + 13, `★ ${starsEarned}/30`, {
        fontFamily: FONT_UI, fontSize: '13px', fontStyle: 'bold', color: CS.gold,
      }).setOrigin(0.5, 0.5));
    }

    const isPrimary = scene === 'NumberKingdom';
    const isAvailable = isPrimary
      || (scene === 'StoryForest' && STORY_FOREST_PRODUCTION_READY)
      || (scene === 'DiscoveryLab' && this.discoveryLabProductionReady());
    const hoverScale = this.reducedMotion ? 1.012 : isPrimary ? 1.06 : locked ? 1.03 : 1.045;
    const lift = this.reducedMotion ? 1 : isPrimary ? 7 : locked ? 4 : 6;
    const glowAlpha = this.reducedMotion ? 0.08 : isPrimary ? 0.22 : locked ? 0.10 : 0.17;
    const tweenContent = (targetY: number, scale: number, duration: number) => {
      this.tweens.killTweensOf(c);
      this.tweens.add({
        targets: c, y: targetY, scaleX: scale, scaleY: scale,
        duration, ease: 'Sine.easeOut',
      });
    };
    const tweenGlow = (alpha: number, duration: number) => {
      this.tweens.killTweensOf(glow);
      this.tweens.add({ targets: glow, alpha, duration, ease: 'Sine.easeOut' });
    };

    c.setInteractive(
      new Phaser.Geom.Rectangle(-HALF_W, -HALF_H * 1.1, HALF_W * 2, HALF_H * 2.2),
      Phaser.Geom.Rectangle.Contains,
    );
    c.on('pointerover', () => {
      if (this.selectedIsland) return;
      tweenContent(-lift, hoverScale, 145);
      tweenGlow(glowAlpha, 145);
      audio.click();
      if (isAvailable) this.say(`Tap to enter ${def.label}!`, 2200);
    });
    c.on('pointerout', () => {
      if (this.selectedIsland) return;
      tweenContent(0, 1, 140);
      tweenGlow(0, 140);
    });
    c.on('pointerdown', () => {
      if (this.selectedIsland) return;
      tweenContent(-lift + 2, Math.max(0.985, hoverScale - 0.025), 60);
    });
    c.on('pointerup', () => {
      if (this.selectedIsland) return;
      if (locked) {
        tweenContent(-lift, hoverScale, 90);
        this.say('This mysterious world is still locked. Coming soon!', 2400);
        return;
      }
      if (!isAvailable) {
        tweenContent(-lift, hoverScale, 90);
        this.say(`${def.label} is coming soon!`, 2200);
        return;
      }
      this.selectedIsland = scene;
      this.enterWorld(def);
    });
    c.on('pointerupoutside', () => {
      tweenContent(0, 1, 90);
      tweenGlow(0, 90);
    });

    return floatRoot;
  }

  // ══════════════════════════════════════════════════════════
  // PLATFORM — approved production art
  // ══════════════════════════════════════════════════════════
  private buildPlatform(W: number, H: number) {
    const targetWidth = W * 0.35;
    const targetHeight = targetWidth * (887 / 1774);
    this.add.image(W * 0.5, H * 0.75, 'home-platform')
        .setDisplaySize(targetWidth, targetHeight)
        .setDepth(50);
  }

  // ══════════════════════════════════════════════════════════
  // COSMO — real art
  // ══════════════════════════════════════════════════════════
  private buildCosmo(W: number, H: number) {
    const cx = W * 0.5, cy = H * 0.72;
    this.cosmoImg = this.add.image(cx, cy, 'cosmo-idle')
        .setOrigin(0.5, 1.0)
        .setScale(this.cosmoBaseScale)
        .setDepth(60);

    if (!this.reducedMotion) {
      this.tweens.add({
        targets: this.cosmoImg, y: cy - 4,
        duration: 3200, ease: 'Sine.easeInOut',
        yoyo: true, repeat: -1,
      });
    }
  }

  private say(text: string, duration = 3000) {
    // Clear any existing bubble
    this.speechText?.destroy();
    this.speechBg?.destroy();
    this.speechTimer?.remove(false);
    this.speechText = null;
    this.speechBg = null;

    const W = LAYOUT_W, H = LAYOUT_H;
    const bubbleW = 234, bubbleH = 54;
    const cx = this.cosmoImg.x;
    const bx = Phaser.Math.Clamp(cx + this.cosmoImg.displayWidth * 0.115, 12, W - bubbleW - 12);
    const by = H * 0.29;

    this.speechBg = this.add.graphics().setDepth(100);
    // Warm, storybook speech card: bright enough for young readers without
    // turning into a large dark HUD panel over the world art.
    this.speechBg.fillStyle(0x7f38e8, 0.16);
    this.speechBg.fillRoundedRect(bx - 7, by - 6, bubbleW + 14, bubbleH + 14, 25);
    this.speechBg.fillStyle(0x12052f, 0.32);
    this.speechBg.fillRoundedRect(bx + 3, by + 6, bubbleW, bubbleH, 22);
    this.speechBg.fillStyle(0xfff8df, 0.98);
    this.speechBg.fillRoundedRect(bx, by, bubbleW, bubbleH, 22);
    this.speechBg.lineStyle(3, 0x8d49dc, 0.95);
    this.speechBg.strokeRoundedRect(bx, by, bubbleW, bubbleH, 22);
    this.speechBg.lineStyle(1, 0xffffff, 0.9);
    this.speechBg.strokeRoundedRect(bx + 4, by + 4, bubbleW - 8, bubbleH - 8, 18);
    this.speechBg.fillStyle(0xffffff, 0.72);
    this.speechBg.fillRoundedRect(bx + 12, by + 7, bubbleW - 24, 9, 6);
    const pointerY = by + bubbleH * 0.68;
    this.speechBg.fillStyle(0x8d49dc, 1);
    this.speechBg.fillTriangle(bx + 3, pointerY - 10, bx + 3, pointerY + 10, bx - 17, pointerY + 16);
    this.speechBg.fillStyle(0xfff8df, 1);
    this.speechBg.fillTriangle(bx + 3, pointerY - 5, bx + 3, pointerY + 7, bx - 11, pointerY + 12);
    this.drawStar(this.speechBg, bx + 19, by + bubbleH * 0.5, 7, 0xffb600, 1);

    this.speechText = this.add.text(bx + bubbleW / 2 + 7, by + bubbleH / 2, text, {
      fontFamily: FONT_UI, fontSize: '13px', fontStyle: 'bold', color: '#2b1657',
      wordWrap: { width: bubbleW - 54 }, align: 'center',
    }).setOrigin(0.5, 0.5).setDepth(101);

    this.speechTimer = this.time.delayedCall(duration, () => {
      this.speechText?.destroy();
      this.speechBg?.destroy();
      this.speechText = null;
      this.speechBg = null;
    });
  }

  private celebrate() {
    const s = this.cosmoBaseScale;
    this.tweens.add({
      targets: this.cosmoImg,
      scaleX: s * 1.14, scaleY: s * 1.14,
      yoyo: true, duration: 240, ease: 'Back.easeOut',
    });
  }

  // ══════════════════════════════════════════════════════════
  // FOREGROUND CRYSTALS — real art
  // ══════════════════════════════════════════════════════════
  private buildForegroundCrystals(W: number, H: number) {
    const targetWidth = W * 0.17;
    const targetHeight = targetWidth * (1086 / 1448);
    const img = this.add.image(W, H, 'cosmic-crystals')
        .setOrigin(1.0, 1.0)
        .setDisplaySize(targetWidth, targetHeight)
        .setDepth(70);
    this.pxFG.add(img);

    // Gentle shimmer
    this.tweens.add({
      targets: img, alpha: { from: 0.94, to: 1.0 },
      duration: 3400, ease: 'Sine.easeInOut',
      yoyo: true, repeat: -1,
    });
  }

  // ══════════════════════════════════════════════════════════
  // UI SYSTEM
  // ══════════════════════════════════════════════════════════
  private buildUI(W: number, H: number) {
    this.buildProfilePanel(W, H);
    this.buildResourcesPanel(W, H);
    this.buildWorldEnergyPanel(W, H);
    this.buildCTAButtons(W, H);
    this.buildBottomNav(W, H);
  }

  private buildProfilePanel(W: number, H: number) {
    const save = SaveManager.getProfile();
    const width = W * 0.207;
    const height = width / 3;
    const panel = this.add.container(8, 8).setDepth(90);
    panel.add(this.add.image(0, 0, 'hud-star-learner')
      .setOrigin(0, 0)
      .setDisplaySize(width, height));
    panel.add(this.add.text(width * 0.585, height * 0.585, `${save.level}`, {
      fontFamily: FONT_UI, fontSize: '14px', fontStyle: 'bold', color: '#fff178',
      stroke: '#3c086d', strokeThickness: 3,
    }).setOrigin(0.5, 0.5));
    panel.add(this.add.text(width * 0.615, height * 0.75, `${save.xp % 200} / 200 XP`, {
      fontFamily: FONT_UI, fontSize: '10px', fontStyle: 'bold', color: '#f7f1ff',
      stroke: '#22004f', strokeThickness: 3,
    }).setOrigin(0.5, 0.5));
  }

  private buildResourcesPanel(W: number, H: number) {
    const save = SaveManager.getProfile();
    const width = 144;
    const height = width / 3;
    const gap = 8;
    const starX = W - 10 - width * 0.5;
    const diamondX = starX - width - gap;
    const y = 10 + height * 0.5;

    const addCounter = (x: number, textureKey: string, value: number, color: string) => {
      const counter = this.add.container(x, y).setDepth(90);
      counter.add(this.add.image(0, 0, textureKey).setDisplaySize(width, height));
      counter.add(this.add.text(width * 0.11, 0, `${value}`, {
        fontFamily: FONT_UI, fontSize: '18px', fontStyle: 'bold', color,
        stroke: '#09002e', strokeThickness: 3,
      }).setOrigin(0.5, 0.5));
    };
    addCounter(diamondX, 'hud-diamonds', save.diamonds, '#bffcff');
    addCounter(starX, 'hud-stars', save.stars, '#fff3a0');
  }

  private buildWorldEnergyPanel(W: number, H: number) {
    const nkEnergy = SaveManager.getWorld('number-kingdom').energy;
    const width = 235;
    const height = width / 3;
    const panel = this.add.container(W * 0.70, H * 0.612).setDepth(90);
    panel.add(this.add.image(0, 0, 'hud-world-energy').setDisplaySize(width, height));
    panel.add(this.add.text(width * 0.11, height * 0.06, `${Math.round(nkEnergy)}/100`, {
      fontFamily: FONT_UI, fontSize: '12px', fontStyle: 'bold', color: '#f8f3ff',
      stroke: '#180042', strokeThickness: 4,
    }).setOrigin(0.5, 0.5));
  }

  private buildCTAButtons(W: number, H: number) {
    const playWidth = W * 0.211;
    const continueWidth = W * 0.242;
    const gap = W * 0.014;
    const rowWidth = playWidth + continueWidth + gap;
    const rowY = H * 0.84;
    const playX = W * 0.5 - rowWidth * 0.5 + playWidth * 0.5;
    const continueX = W * 0.5 + rowWidth * 0.5 - continueWidth * 0.5;
    const numberKingdom = this.numberKingdomDestination();
    const storyForest = this.storyForestDestination();
    const discoveryLab = this.discoveryLabDestination();

    this.createProductionButton({
      x: playX, y: rowY, width: playWidth, textureKey: 'home-play', depth: 80,
      hoverScale: 1.03,
      onActivate: () => {
        if (this.selectedIsland) return;
        this.selectedIsland = 'NumberKingdom';
        this.enterWorld(numberKingdom);
      },
    });

    const lastScene = SaveManager.getLastScene();
    const continueDestination = lastScene === 'StoryForest' && STORY_FOREST_PRODUCTION_READY
      ? storyForest
      : lastScene === 'DiscoveryLab' && this.discoveryLabProductionReady()
        ? discoveryLab
        : lastScene === MATERIALS_WORKSHOP_SCENE_KEY && canEnterMaterialsWorkshop(this.textures)
          ? { ...discoveryLab, scene: MATERIALS_WORKSHOP_SCENE_KEY, label: 'Materials Workshop' }
        : lastScene === WEATHER_WATCH_SCENE_KEY && canEnterWeatherWatchStation(this.textures)
          ? { ...discoveryLab, scene: WEATHER_WATCH_SCENE_KEY, label: 'Weather Watch Station' }
        : lastScene === HABITAT_FIELD_SCENE_KEY && canEnterHabitatFieldCamp(this.textures)
          ? { ...discoveryLab, scene: HABITAT_FIELD_SCENE_KEY, label: 'Habitat Field Camp' }
        : lastScene === PLANT_GROWTH_SCENE_KEY && canEnterPlantGrowthHouse(this.textures)
          ? { ...discoveryLab, scene: PLANT_GROWTH_SCENE_KEY, label: 'Plant Growth House' }
        : lastScene === LIFE_CYCLE_SCENE_KEY && canEnterLifeCyclePond(this.textures)
          ? { ...discoveryLab, scene: LIFE_CYCLE_SCENE_KEY, label: 'Life Cycle Pond' }
        : lastScene === SOUND_VIBRATION_SCENE_KEY && canEnterSoundVibrationDeck(this.textures)
          ? { ...discoveryLab, scene: SOUND_VIBRATION_SCENE_KEY, label: 'Sound & Vibration Deck' }
        : lastScene === EARTH_MATERIALS_SCENE_KEY && canEnterEarthMaterialsYard(this.textures)
          ? { ...discoveryLab, scene: EARTH_MATERIALS_SCENE_KEY, label: 'Earth Materials Yard' }
        : lastScene === DISCOVERY_OBSERVATORY_SCENE_KEY && canEnterDiscoveryObservatory(this.textures)
          ? { ...discoveryLab, scene: DISCOVERY_OBSERVATORY_SCENE_KEY, label: 'Discovery Observatory' }
        : lastScene === CHARACTER_GROVE_SCENE_KEY && canEnterCharacterGrove(this.textures)
          ? { ...storyForest, scene: CHARACTER_GROVE_SCENE_KEY, label: 'Character Grove' }
        : lastScene === SEQUENCE_STREAM_SCENE_KEY && canEnterSequenceStream(this.textures)
          ? { ...storyForest, scene: SEQUENCE_STREAM_SCENE_KEY, label: 'Sequence Stream' }
        : lastScene === MAIN_IDEA_MEADOW_SCENE_KEY && canEnterMainIdeaMeadow(this.textures)
          ? { ...storyForest, scene: MAIN_IDEA_MEADOW_SCENE_KEY, label: 'Main Idea Meadow' }
        : lastScene === INFERENCE_HOLLOW_SCENE_KEY && canEnterInferenceHollow(this.textures)
          ? { ...storyForest, scene: INFERENCE_HOLLOW_SCENE_KEY, label: 'Inference Hollow' }
        : lastScene === AUTHORS_CABIN_SCENE_KEY && canEnterAuthorsCabin(this.textures)
          ? { ...storyForest, scene: AUTHORS_CABIN_SCENE_KEY, label: "Author's Cabin" }
        : lastScene === GRAMMAR_GLADE_SCENE_KEY && canEnterGrammarGlade(this.textures)
          ? { ...storyForest, scene: GRAMMAR_GLADE_SCENE_KEY, label: 'Grammar Glade' }
        : lastScene === FLUENCY_LANTERN_SCENE_KEY && canEnterFluencyLanternPath(this.textures)
          ? { ...storyForest, scene: FLUENCY_LANTERN_SCENE_KEY, label: 'Fluency Lantern Path' }
        : lastScene === GREAT_STORY_TREE_SCENE_KEY && canEnterGreatStoryTree(this.textures)
          ? { ...storyForest, scene: GREAT_STORY_TREE_SCENE_KEY, label: 'Great Story Tree' }
        : lastScene === REGROUPING_WORKSHOP_SCENE_KEY && canEnterRegroupingWorkshop(this.textures)
          ? { ...numberKingdom, scene: REGROUPING_WORKSHOP_SCENE_KEY, label: 'Regrouping Workshop' }
        : lastScene === SUBTRACTION_KEEP_SCENE_KEY && canEnterSubtractionKeep(this.textures)
          ? { ...numberKingdom, scene: SUBTRACTION_KEEP_SCENE_KEY, label: 'Subtraction Keep' }
        : lastScene === MEASUREMENT_MARKET_SCENE_KEY && canEnterMeasurementMarket(this.textures)
          ? { ...numberKingdom, scene: MEASUREMENT_MARKET_SCENE_KEY, label: 'Measurement Market' }
        : lastScene === CLOCKTOWER_PLAZA_SCENE_KEY && canEnterClocktowerPlaza(this.textures)
          ? { ...numberKingdom, scene: CLOCKTOWER_PLAZA_SCENE_KEY, label: 'Clocktower Plaza' }
        : lastScene === COINMAKERS_VAULT_SCENE_KEY && canEnterCoinmakersVault(this.textures)
          ? { ...numberKingdom, scene: COINMAKERS_VAULT_SCENE_KEY, label: "Coinmaker's Vault" }
        : lastScene === ROYAL_CHALLENGE_HALL_SCENE_KEY && canEnterRoyalChallengeHall(this.textures)
          ? { ...numberKingdom, scene: ROYAL_CHALLENGE_HALL_SCENE_KEY, label: 'Royal Challenge Hall' }
        : lastScene === NUMBER_FORGE_SCENE_KEY && canEnterNumberForge(this.textures)
          ? { ...numberKingdom, scene: NUMBER_FORGE_SCENE_KEY, label: 'Number Forge' }
        : lastScene === 'StarBridge'
          ? { ...numberKingdom, scene: 'StarBridge', label: 'Star Bridge' }
          : numberKingdom;
    const continueScene = continueDestination.scene;
    const continueLabel = continueDestination.label;
    const continueButton = this.createProductionButton({
      x: continueX, y: rowY, width: continueWidth, textureKey: 'home-continue', depth: 80,
      hoverScale: 1.025,
      onActivate: () => {
        if (this.selectedIsland) return;
        this.selectedIsland = continueScene;
        this.enterWorld(continueDestination);
      },
    });
    const continueHeight = continueWidth / 3;
    continueButton.add(this.add.text(-continueWidth * 0.04, continueHeight * 0.18,
      `Last played: ${continueLabel}`, {
        fontFamily: FONT_UI, fontSize: '11px', fontStyle: 'bold', color: '#f3edff',
        shadow: { offsetX: 0, offsetY: 1, blur: 2, color: '#21005e', fill: true },
      }).setOrigin(0.5, 0.5));
  }

  private buildBottomNav(W: number, H: number) {
    const numberKingdom = this.numberKingdomDestination();
    const storyForest = this.storyForestDestination();
    const nk = SaveManager.getWorld('number-kingdom');
    const dailyProgress = Phaser.Math.Clamp(nk.bridgeSegments, 0, 4);
    const collectedRewards = nk.collectibles.length;
    const navItems = [
      { key: 'nav-daily', action: () => this.say(`Daily Mission: ${dailyProgress} of 4 bridge sections powered!`, 2600) },
      { key: 'nav-math', action: () => {
        if (this.selectedIsland) return;
        this.selectedIsland = 'NumberKingdom';
        this.enterWorld(numberKingdom);
      } },
      { key: 'nav-reading', action: () => {
        if (!STORY_FOREST_PRODUCTION_READY) {
          this.say('Reading World is coming soon!', 2200);
          return;
        }
        if (this.selectedIsland) return;
        this.selectedIsland = 'StoryForest';
        this.enterWorld(storyForest);
      } },
      { key: 'nav-rewards', action: () => this.say(
        collectedRewards > 0
          ? `${collectedRewards} constellation reward${collectedRewards === 1 ? '' : 's'} collected!`
          : 'Complete missions to earn cosmic rewards!', 2400) },
      { key: 'nav-parent', action: () => this.say('Parent Area is coming soon!', 2200) },
      { key: 'nav-settings', action: () => this.say('Settings are coming soon!', 2200) },
    ];

    const navHeight = H * 0.09;
    const navWidth = navHeight * 2;
    const navY = H * 0.952;
    navItems.forEach(({ key, action }, index) => {
      const button = this.createProductionButton({
        x: W * ((index + 0.5) / navItems.length), y: navY,
        width: navWidth, textureKey: key, depth: 85, onActivate: action,
        hoverScale: 1.025,
      });
      if (key === 'nav-daily') {
        button.add(this.add.text(navWidth * 0.055, navHeight * 0.295, `${dailyProgress}/4`, {
          fontFamily: FONT_UI, fontSize: '10px', fontStyle: 'bold', color: '#ffffff',
          shadow: { offsetX: 0, offsetY: 1, blur: 2, color: '#150044', fill: true },
        }).setOrigin(0.5, 0.5));
      }
    });
  }

  private createProductionButton(options: ProductionButtonOptions) {
    const { x, y, width, textureKey, depth, onActivate } = options;
    const button = this.add.container(x, y).setDepth(depth);
    const image = this.add.image(0, 0, textureKey);
    const height = width * (image.height / image.width);
    image.setDisplaySize(width, height);
    button.add(image);

    const hoverScale = this.reducedMotion ? 1.01 : (options.hoverScale ?? 1.025);
    const hoverY = y - (this.reducedMotion ? 1 : 3);
    const tweenTo = (targetY: number, scale: number, duration: number) => {
      this.tweens.killTweensOf(button);
      this.tweens.add({
        targets: button, y: targetY, scaleX: scale, scaleY: scale,
        duration, ease: 'Sine.easeOut',
      });
    };

    button.setInteractive(
      new Phaser.Geom.Rectangle(-width * 0.5, -height * 0.5, width, height),
      Phaser.Geom.Rectangle.Contains,
    );
    button.on('pointerover', () => {
      tweenTo(hoverY, hoverScale, 130);
      audio.click();
    });
    button.on('pointerout', () => tweenTo(y, 1, 120));
    button.on('pointerdown', () => tweenTo(y + 1, this.reducedMotion ? 0.99 : 0.98, 60));
    button.on('pointerup', () => {
      tweenTo(hoverY, hoverScale, 90);
      onActivate();
    });
    button.on('pointerupoutside', () => tweenTo(y, 1, 90));
    return button;
  }

  private numberKingdomDestination(): IslandDef {
    return {
      scene: 'NumberKingdom', label: 'Number Kingdom', glowColor: 0xff8c00,
      locked: false, world: 'number-kingdom', x: 0, y: 0, textureKey: 'number-kingdom',
    };
  }

  private storyForestDestination(): IslandDef {
    return {
      scene: 'StoryForest', label: 'Story Forest', glowColor: 0x00c988,
      locked: false, world: 'story-forest', x: 0, y: 0, textureKey: 'story-forest',
    };
  }

  private discoveryLabDestination(): IslandDef {
    return {
      scene: 'DiscoveryLab', label: 'Discovery Lab', glowColor: 0x00c8e8,
      locked: false, world: 'discovery-lab', x: 0, y: 0, textureKey: 'discovery-lab',
    };
  }

  private discoveryLabProductionReady() {
    return DISCOVERY_LAB_ROUTE_ENABLED && isDiscoveryLabOpeningReady(this.textures);
  }

  // ══════════════════════════════════════════════════════════
  // PARALLAX
  // ══════════════════════════════════════════════════════════
  private setupParallax() {
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      const dx = pointer.x / this.scale.width  - 0.5;
      const dy = pointer.y / this.scale.height - 0.5;
      this.pxIslands.x = dx * -6; this.pxIslands.y = dy * -4;
      this.pxFG.x      = dx * -10; this.pxFG.y      = dy * -7;
    });
  }

  // ══════════════════════════════════════════════════════════
  // HELPERS
  // ══════════════════════════════════════════════════════════
  private drawStar(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, col: number, alpha: number) {
    const pts: Phaser.Types.Math.Vector2Like[] = [];
    const inner = r * 0.42;
    for (let i = 0; i < 10; i++) {
      const a = (i * Math.PI / 5) - Math.PI / 2;
      const radius = i % 2 === 0 ? r : inner;
      pts.push({ x: cx + Math.cos(a) * radius, y: cy + Math.sin(a) * radius });
    }
    g.fillStyle(col, alpha);
    g.fillPoints(pts, true);
  }

  // ══════════════════════════════════════════════════════════
  // EVENTS
  // ══════════════════════════════════════════════════════════
  private flickerNK() {
    const nk = this.islands[0];
    if (!nk) return;
    let count = 0;
    const flicker = () => {
      nk.setAlpha(nk.alpha < 0.85 ? 1 : 0.65);
      count++;
      if (count < 8) this.time.delayedCall(140 + Math.random() * 180, flicker);
      else {
        nk.setAlpha(1);
        this.time.delayedCall(400, () => {
          this.say('Oh no! Number Kingdom is losing power! We need to help!', 3500);
        });
      }
    };
    flicker();
  }

  private enterWorld(def: IslandDef) {
    audio.portalActivate();
    this.celebrate();
    this.input.enabled = false;
    const transitionDuration = this.reducedMotion ? 260 : 620;
    this.cameras.main.fadeOut(transitionDuration, 255, 248, 220);
    this.time.delayedCall(transitionDuration + 20, () => {
      if (!def.scene) return;
      this.scale.setGameSize(LAYOUT_W, LAYOUT_H);
      this.cameras.main.setZoom(1).centerOn(LAYOUT_W * 0.5, LAYOUT_H * 0.5);
      this.scene.start(def.scene);
    });
  }
}
